import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useBrand } from '@/hooks/useBrand';

export type LegalDoc = 'confidentialite' | 'conditions';

interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

interface LegalContent {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

const CONTENT: Record<LegalDoc, LegalContent> = {
  confidentialite: {
    title: 'Politique de confidentialité',
    updated: 'Dernière mise à jour : octobre 2026',
    intro:
      "Chez ZIFEK, la protection de vos données personnelles est une priorité. Cette politique explique quelles données nous collectons, pourquoi et comment vous pouvez garder le contrôle.",
    sections: [
      {
        heading: '1. Données que nous collectons',
        paragraphs: [
          'Nous ne collectons que les informations nécessaires au fonctionnement de la plateforme et à la fourniture de nos services.',
        ],
        bullets: [
          "Informations de compte : nom, adresse e-mail, numéro de téléphone, nom du commerce.",
          'Données de navigation : pages visitées, provenance (moteur de recherche, réseaux sociaux), adresse IP pour la sécurité et la localisation approximative.',
          'Contenus que vous créez : produits, services, images, commandes et messages.',
        ],
      },
      {
        heading: '2. Utilisation des données',
        paragraphs: [
          'Vos données servent uniquement à :',
        ],
        bullets: [
          'Créer et gérer votre compte et vos boutiques ;',
          'Traiter vos commandes et réservations ;',
          'Améliorer nos services et la pertinence de l’assistant IA ;',
          'Vous envoyer des notifications liées à votre activité ;',
          'Garantir la sécurité et lutter contre la fraude.',
        ],
      },
      {
        heading: '3. Partage des données',
        paragraphs: [
          'Nous ne vendons jamais vos données. Elles peuvent être partagées uniquement avec les prestataires techniques strictement nécessaires (hébergement, paiement, e-mail) et toujours dans le respect de vos droits.',
        ],
      },
      {
        heading: '4. Vos droits',
        paragraphs: [
          'Vous pouvez à tout moment accéder à vos données, les corriger, les exporter ou demander leur suppression depuis votre espace ou en nous contactant.',
        ],
      },
      {
        heading: '5. Cookies',
        paragraphs: [
          'Nous utilisons des cookies essentiels au fonctionnement du site et, avec votre accord, des cookies de mesure d’audience. Vous pouvez les gérer à tout moment depuis votre navigateur.',
        ],
      },
    ],
  },
  conditions: {
    title: "Conditions générales d'utilisation",
    updated: 'Dernière mise à jour : octobre 2026',
    intro:
      "Les présentes conditions régissent l'accès et l'utilisation de la plateforme ZIFEK. En créant un compte ou en utilisant nos services, vous les acceptez sans réserve.",
    sections: [
      {
        heading: '1. Objet du service',
        paragraphs: [
          'ZIFEK permet à tout utilisateur de créer une boutique en ligne, de vendre des produits et des services, de gérer des réservations et d’utiliser des outils assistés par intelligence artificielle.',
        ],
      },
      {
        heading: '2. Compte utilisateur',
        paragraphs: [
          'Vous êtes responsable de la confidentialité de vos identifiants et de l’exactitude des informations fournies lors de l’inscription. Toute activité réalisée depuis votre compte vous est imputable.',
        ],
      },
      {
        heading: '3. Obligations de l’utilisateur',
        paragraphs: ['En utilisant ZIFEK, vous vous engagez à :'],
        bullets: [
          'Publier des contenus licites et conformes à la réglementation ;',
          'Ne pas porter atteinte aux droits de tiers (images, marques, données) ;',
          'Honorer les commandes et réservations acceptées auprès de vos clients ;',
          'Ne pas utiliser la plateforme à des fins frauduleuses ou abusives.',
        ],
      },
      {
        heading: '4. Paiements et abonnements',
        paragraphs: [
          'Certaines fonctionnalités sont soumises à un abonnement. Les montants, périodes d’essai et modalités de facturation sont indiqués dans votre espace. ZIFEK ne prélève aucune commission sur vos ventes au-delà des frais éventuels de vos passerelles de paiement.',
        ],
      },
      {
        heading: '5. Responsabilité',
        paragraphs: [
          'ZIFEK met la plateforme à disposition « en l’état » et met tout en œuvre pour assurer sa disponibilité. Nous ne saurions être tenus responsables des contenus publiés par les utilisateurs ni des transactions réalisées entre eux.',
        ],
      },
      {
        heading: '6. Résiliation',
        paragraphs: [
          'Vous pouvez fermer votre compte à tout moment. ZIFEK se réserve le droit de suspendre un compte en cas de manquement grave aux présentes conditions.',
        ],
      },
    ],
  },
};

interface LegalPageProps {
  doc: LegalDoc;
}

export default function LegalPage({ doc }: LegalPageProps) {
  const { brand } = useBrand();
  const { t } = useTranslation();
  const content = CONTENT[doc];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-3xl mx-auto">
          <div className="mb-8 md:mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              <i className="ri-file-shield-2-line"></i>
              {brand.name || 'ZIFEK'}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {content.title}
            </h1>
            <p className="text-xs text-foreground-400">{content.updated}</p>
          </div>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">
            {content.intro}
          </p>

          <div className="space-y-8">
            {content.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-3">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-2">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-2 space-y-2">
                    {section.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm md:text-base text-foreground-600">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-sm text-foreground-500">
              Une question ? Écrivez-nous à{' '}
              <a href="mailto:contact@zifek.fr" className="text-primary-600 hover:text-primary-700 font-medium no-underline">
                contact@zifek.fr
              </a>
              . {t('legal.contact_hint', { defaultValue: 'Notre équipe vous répond sous 48 h.' })}
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}